import { useCallback, useEffect, useState } from 'react';
import { BERICHTEN_BEWAAR_DAGEN, isVasteKamer } from '@tolk/shared';
import { ApiFout, leesKosten, wijzigKamer, type KostenStand } from '../lib/api';
import { verlooptTekst } from '../lib/verlooptijd';

/**
 * Wat dit gesprek en deze maand tot nu toe kosten. ALLEEN VOOR DE BEHEERDER.
 *
 * NEDERLANDS EN NIET VERTAALD, met opzet. Hetzelfde geldt voor het scherm om
 * een kamer aan te maken: dat leest alleen de beheerder, en die spreekt
 * Nederlands. Vertalen wat niemand leest, kost onderhoud zonder opbrengst.
 *
 * De balk verdwijnt zodra de server hem niet geeft - in productie staat de
 * beheerpoort dicht tot stap 15, en een verlopen kamer geeft 404. Een
 * kostenbalk is nooit belangrijk genoeg om een gesprek voor te onderbreken.
 */

/** Hoeveel uur "verlengen" erbij doet. Gelijk aan de standaardgeldigheid. */
const VERLENG_UREN = 4;
/** Zeven dagen vanaf nu; daarboven weigert de server. */
const MAX_VOORUIT_MS = 168 * 3600_000;

/**
 * Bedrag in euro, Nederlands genoteerd.
 *
 * Onder de cent komen er DRIE cijfers achter de komma in plaats van twee.
 * Anders staat er "0,00" bij een gesprek dat wel degelijk geld kostte. Eerst
 * stond hier "< € 0,01", maar die tekst gold ook voor een PLAFOND, en dan las
 * de regel als "< € 0,01 van < € 0,01" - twee keer hetzelfde teken voor twee
 * verschillende dingen. Gemeten op het scherm, 6 september 2026.
 */
function euro(bedrag: number): string {
  const cijfers = bedrag > 0 && bedrag < 0.01 ? 3 : 2;
  return `€ ${bedrag.toLocaleString('nl-NL', {
    minimumFractionDigits: cijfers,
    maximumFractionDigits: cijfers,
  })}`;
}

function deel(bedrag: number, plafond: number): number {
  if (plafond <= 0) return 0;
  return Math.min(bedrag / plafond, 1);
}

const MELDING: Record<NonNullable<KostenStand['waarschuwing']>, string> = {
  maand: 'De maandgrens komt in zicht. Daarna stoppen alle gesprekken.',
  kamer: 'De grens van dit gesprek komt in zicht. Daarna stopt dit gesprek.',
  dag: 'Je zit boven het dagbedrag. Dit is alleen een seintje, er stopt niets.',
};

/**
 * Een regel: waar het over gaat links, wat het kost rechts.
 *
 * HET BEDRAG EN ZIJN PLAFOND BLIJVEN BIJ ELKAAR. Ze stonden eerst twee naast
 * elkaar op een rij, en op een telefoon van 375 px paste dat net niet: 303 px
 * tekst in 315 px. De regel brak dan middenin, waardoor "van € 30,00" op een
 * eigen regel belandde en las alsof het bij het bedrag eronder hoorde. Met
 * bedragen van een cent viel dat niet op; met "€ 29,87 van € 30,00" wel.
 * Gemeten 13 september 2026 met de bedragen uit de database, niet met nullen.
 *
 * Nu een regel per bedrag, en `whitespace-nowrap` zodat het paar nooit meer
 * uit elkaar valt. Dat kost hoogte, en dat mag: de kaart scrolt.
 */
function Regel({
  naam,
  bedrag,
  plafond,
}: {
  naam: string;
  bedrag: number;
  plafond: number;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[11px] text-slate-550">
      <span>{naam}</span>
      <span className="whitespace-nowrap">
        <span className="font-mono text-slate-650">{euro(bedrag)}</span>
        <span className="text-slate-350"> van {euro(plafond)}</span>
      </span>
    </div>
  );
}

function Meter({ deelVol, waarschuwt }: { deelVol: number; waarschuwt: boolean }) {
  return (
    <div className="h-1 bg-slate-150 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-300 ${
          waarschuwt ? 'bg-gio-orange' : 'bg-gio-blue'
        }`}
        style={{ width: `${Math.round(deelVol * 100)}%` }}
      />
    </div>
  );
}

/**
 * De kostenstand van een kamer, voor de maker.
 *
 * EEN HOOK EN NIET BINNEN DE BALK, omdat de kamer hem op TWEE plekken nodig
 * heeft: onderaan in de balk, en bovenaan als waarschuwing zodra het plafond in
 * zicht komt. Onderaan alleen was te laat: op 22 september 2026 liep een
 * gesprek vol zonder dat iemand het regeltje zag.
 *
 * `aantalBerichten`: verandert dat, dan is er geld uitgegeven en wordt de
 * stand opnieuw opgehaald. Geen klok die eeuwig doorloopt: er valt niets te
 * verversen zolang er niets gebeurt.
 */
export function useKostenStand(
  kamerId: string,
  aantalBerichten: number,
  actief: boolean,
): { stand: KostenStand | null; ververs: () => void } {
  const [stand, setStand] = useState<KostenStand | null>(null);
  // Loopt op na een eigen wijziging, zodat de stand meteen opnieuw komt.
  const [tik, setTik] = useState(0);
  const ververs = useCallback(() => setTik((n) => n + 1), []);

  useEffect(() => {
    if (!actief) return;
    let levend = true;

    async function ophalen(): Promise<void> {
      try {
        const nieuw = await leesKosten(kamerId);
        // Niet meer zetten na het opruimen: dan schrijf je in een component
        // die van het scherm af is.
        if (levend) setStand(nieuw);
      } catch {
        // Poort dicht, kamer verlopen, netwerk weg. Alle drie hetzelfde
        // antwoord: geen balk. De vorige stand blijft staan als die er was,
        // want een bedrag van tien seconden geleden is beter dan niets.
      }
    }

    void ophalen();

    // Spraak wordt NA het bericht gemaakt en kost apart geld. Eén keer kijken
    // op het moment dat het bericht binnenkomt, is dus te vroeg.
    const later = window.setTimeout(() => void ophalen(), 5000);

    return () => {
      levend = false;
      window.clearTimeout(later);
    };
  }, [kamerId, aantalBerichten, actief, tik]);

  return { stand, ververs };
}

/** Is het kamerplafond bereikt? Dan staat het gesprek stil. */
export function kamerVol(stand: KostenStand): boolean {
  return stand.kamerEur >= stand.kamerPlafondEur;
}

/**
 * De knoppen om het kamerplafond op te hogen.
 *
 * Het nieuwe bedrag is HET HUIDIGE PLAFOND PLUS de stap, niet wat er al
 * uitgegeven is plus de stap. Anders geeft "+ EUR 1" op een half volle kamer
 * minder dan een euro ruimte erbij.
 *
 * Boven het maandbudget kan niet; die knop valt dan weg in plaats van een
 * foutmelding te geven waar je niets mee kunt.
 */
export function Ophoogknoppen({
  kamerId,
  stand,
  ververs,
  opvallend = false,
}: {
  kamerId: string;
  stand: KostenStand;
  ververs: () => void;
  opvallend?: boolean;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  async function ophogen(stap: number): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      // Afronden op centen: 0,5 + 0,1 is in JavaScript 0,6000000000000001.
      const nieuw = Math.round((stand.kamerPlafondEur + stap) * 100) / 100;
      await wijzigKamer(kamerId, { budgetEur: nieuw });
      ververs();
    } catch (error) {
      setFout(error instanceof ApiFout ? error.message : 'Ophogen lukte niet.');
    } finally {
      setBezig(false);
    }
  }

  const stappen = [1, 5].filter((stap) => stand.kamerPlafondEur + stap <= stand.maandPlafondEur);
  if (stappen.length === 0) return null;

  return (
    <div>
      <div className="flex gap-2">
        {stappen.map((stap) => (
          <button
            key={stap}
            type="button"
            disabled={bezig}
            onClick={() => void ophogen(stap)}
            className={`flex-1 rounded-lg font-bold transition-colors duration-155 disabled:opacity-50
              ${
                opvallend
                  ? 'py-2 text-xs bg-white text-gio-orange-deep hover:bg-gio-orange/10'
                  : 'py-1.5 text-[11px] border-2 border-slate-250 text-slate-650 hover:border-gio-blue hover:text-gio-blue'
              }`}
          >
            + € {stap}
          </button>
        ))}
      </div>
      {fout !== null && <p className="mt-1 text-[11px] text-gio-danger">{fout}</p>}
    </div>
  );
}

/**
 * Bovenaan in de kamer, alleen voor de maker: het plafond komt in zicht, of
 * het gesprek staat al stil. Met de knop erin, want de melding is pas nuttig
 * als je er meteen iets mee kunt.
 */
export function Budgetwaarschuwing({
  kamerId,
  stand,
  ververs,
}: {
  kamerId: string;
  stand: KostenStand;
  ververs: () => void;
}) {
  const vol = kamerVol(stand);
  if (!vol && stand.waarschuwing !== 'kamer') return null;

  return (
    <div
      role="status"
      className="mt-2 p-3 rounded-xl bg-gio-orange text-white shadow-md animate-fade-in space-y-2"
    >
      <p className="text-xs font-bold leading-relaxed">
        {vol
          ? `Dit gesprek staat stil: het plafond van ${euro(stand.kamerPlafondEur)} is bereikt. Verhoog het, dan gaat het met dezelfde link verder.`
          : `Dit gesprek zit op ${euro(stand.kamerEur)} van ${euro(stand.kamerPlafondEur)}. Verhoog nu, dan stopt het niet.`}
      </p>
      <Ophoogknoppen kamerId={kamerId} stand={stand} ververs={ververs} opvallend />
    </div>
  );
}

/**
 * Verlengen met een tik. Rekent vanaf de HUIDIGE eindtijd, niet vanaf nu: wie
 * twee keer tikt, krijgt er twee keer vier uur bij.
 */
function Verlengen({
  kamerId,
  expiresAt,
  ververs,
}: {
  kamerId: string;
  expiresAt: number;
  ververs: () => void;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const vast = isVasteKamer(expiresAt);
  const nieuw = expiresAt + VERLENG_UREN * 3600_000;
  const kan = !vast && nieuw <= Date.now() + MAX_VOORUIT_MS;

  async function stuur(wijziging: { expiresAt: number } | { vast: boolean }): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      await wijzigKamer(kamerId, wijziging);
      ververs();
    } catch (error) {
      setFout(error instanceof ApiFout ? error.message : 'Dat lukte niet.');
    } finally {
      setBezig(false);
    }
  }

  const knop = `shrink-0 px-2.5 py-1 rounded-lg border-2 border-slate-250 font-bold
                text-slate-650 hover:border-gio-blue hover:text-gio-blue
                transition-colors duration-155 disabled:opacity-50`;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 text-[11px] text-slate-550">
        <span>verloopt {verlooptTekst(expiresAt, Date.now())}</span>
        <span className="flex gap-1.5">
          {kan && (
            <button
              type="button"
              disabled={bezig}
              onClick={() => void stuur({ expiresAt: nieuw })}
              className={knop}
            >
              + {VERLENG_UREN} uur
            </button>
          )}
          {/*
            Vastzetten en losmaken zijn allebei omkeerbaar, dus geen
            bevestigingsvraag. Losmaken geeft de gewone looptijd vanaf nu.
          */}
          <button
            type="button"
            disabled={bezig}
            onClick={() => void stuur({ vast: !vast })}
            className={knop}
          >
            {vast ? 'Einddatum zetten' : 'Vastzetten'}
          </button>
        </span>
      </div>
      {vast && (
        <p className="mt-1 text-[11px] text-slate-450 leading-relaxed">
          Blijft open tot je hem sluit. Berichten blijven {BERICHTEN_BEWAAR_DAGEN} dagen bewaard.
        </p>
      )}
      {fout !== null && <p className="mt-1 text-[11px] text-gio-danger">{fout}</p>}
    </div>
  );
}

export default function Kostenbalk({
  kamerId,
  stand,
  ververs,
  expiresAt,
}: {
  kamerId: string;
  stand: KostenStand | null;
  ververs: () => void;
  expiresAt: number;
}) {
  if (stand === null) return null;

  const waarschuwt = stand.waarschuwing !== null;

  return (
    <section
      className="mt-1.5 border-t border-slate-150 pt-2 space-y-1.5"
      aria-label="Kosten van dit gesprek"
    >
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-450">Kosten</p>

      <Regel naam="dit gesprek" bedrag={stand.kamerEur} plafond={stand.kamerPlafondEur} />

      <Meter
        deelVol={deel(stand.kamerEur, stand.kamerPlafondEur)}
        waarschuwt={stand.waarschuwing === 'kamer'}
      />

      <Ophoogknoppen kamerId={kamerId} stand={stand} ververs={ververs} />

      {/*
        VANDAAG HEEFT GEEN METER, EN DAT IS EEN BESLUIT. De daggrens stopt
        niets; hij geeft alleen een seintje. Een balk ernaast zou hem eruit
        laten zien als de twee grenzen die het gesprek wel afsluiten.

        De maandmeter staat daarom DIRECT onder "deze maand". Naast elkaar op
        een rij hoorde je aan niets welke van de twee bij de balk eronder
        hoorde.
      */}
      <Regel naam="vandaag" bedrag={stand.dagEur} plafond={stand.dagPlafondEur} />

      <Regel naam="deze maand" bedrag={stand.maandEur} plafond={stand.maandPlafondEur} />

      <Meter
        deelVol={deel(stand.maandEur, stand.maandPlafondEur)}
        waarschuwt={stand.waarschuwing === 'maand'}
      />

      {waarschuwt && (
        <p className="text-[11px] leading-relaxed text-gio-orange-deep animate-fade-in">
          {MELDING[stand.waarschuwing as NonNullable<KostenStand['waarschuwing']>]}
        </p>
      )}

      <div className="border-t border-slate-150 pt-1.5 space-y-1.5">
        <Verlengen kamerId={kamerId} expiresAt={expiresAt} ververs={ververs} />
      </div>

      <p className="text-[10px] leading-relaxed text-slate-450">
        Alleen jij ziet dit. Je gesprekspartner niet.
      </p>
    </section>
  );
}
